import axiosClient from "utils/axiosClient.util";
import { toastEmitter } from "utils/toast.util";
import {
  IZnsTemplateListParams,
  IZnsTemplateListResponse,
  IZnsTemplate,
} from "../types/znsTemplate";

/**
 * Fetches the list of ZNS templates from GET /api/ZnsTemplate/v1.
 * @param params - Optional query params (e.g. page, limit / pageNumber, pageSize)
 * @returns List of templates and total count { results, totalCount }
 */
function getZnsTemplates(
  params?: IZnsTemplateListParams
): Promise<{ results: IZnsTemplate[]; totalCount: number }> {
  return axiosClient.get("/api/ZnsTemplate/v1", { params }).then((res: unknown) => {
    const data = res as IZnsTemplateListResponse;
    return {
      results: Array.isArray(data?.results) ? data.results : [],
      totalCount: typeof data?.totalCount === "number" ? data.totalCount : 0,
      lastPulledDate: data?.lastPulledDate ?? "",
    };
  });
}

/**
 * Triggers sync of ZNS templates from Zalo OA via POST /api/ZnsTemplate/v1/sync.
 * @returns Promise resolving to the API response
 */
function syncZnsTemplates(): Promise<unknown> {
  return axiosClient.post("/api/ZnsTemplate/v1/sync");
}

/**
 * Downloads the sample data file for a ZNS template via GET /api/ZnsTemplate/v1/{id}/sample-data.
 * Triggers a browser file download.
 */
async function downloadSampleData(id: string): Promise<void> {
  try {
    const response = await axiosClient.get(
      `/api/ZnsTemplate/v1/${id}/sample-data`,
      { responseType: "text" }
    );
    const csvContent = response as unknown as string;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", `sample-data-${id}.csv`);
    a.click();
    URL.revokeObjectURL(url);
  } catch (err: any) {
    const msg =
      err?.message || err?.Message || "Download failed. Please try again.";
    toastEmitter.show(msg, "error");
    throw err;
  }
}

const ZnsTemplateService = {
  getZnsTemplates,
  syncZnsTemplates,
  downloadSampleData,
};

export default ZnsTemplateService;
